/**
 * Dataset Queries Slice
 *
 * GW-1340: State management for query delivery feature
 * GW-1459: Added fetchAllQueries for organization-wide query history
 * Handles query submission, listing, and status tracking
 */

import {
  setLoading,
  setError,
  clearError as clearOpError,
  isOperationLoading,
  hasOperationLoaded,
} from '@gw/ui';

import { datasetsApi } from '@/api/datasets';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';

import type {
  DatasetQuery,
  DatasetQueryResponse,
  SubmitQueryRequest,
  SubmitQueryResponse,
  ListQueriesParams,
  ListQueriesResponse,
  LoadingStates,
  LoadedTimestamps,
  ErrorStates,
  ErrorStatuses,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

export interface DatasetQueriesSlice {
  // State
  // List endpoints return the DatasetQueryResponse projection (queryId, tokens*
  // scalars); getQueryById returns the full DatasetQuery aggregate.
  datasetQueries: DatasetQueryResponse[];
  allQueries: DatasetQueryResponse[]; // GW-1459: All queries across datasets
  currentQuery: DatasetQuery | null;
  queriesTotal: number;
  allQueriesTotal: number; // GW-1459: Total count for all queries
  queriesIsLoading: LoadingStates;
  queriesHasLoaded: LoadingStates;
  queriesLastLoaded: LoadedTimestamps;
  queriesErrors: ErrorStates;
  queriesErrorStatuses: ErrorStatuses;

  // Actions
  fetchQueries: (datasetId: string, params?: ListQueriesParams) => Promise<void>;
  fetchAllQueries: (params?: ListQueriesParams) => Promise<void>; // GW-1459
  fetchQueryById: (queryId: string) => Promise<void>;
  submitQuery: (datasetId: string, request: SubmitQueryRequest) => Promise<SubmitQueryResponse>;
  clearQueries: () => void;
  clearAllQueries: () => void; // GW-1459
  clearCurrentQuery: () => void;
  clearQueriesError: (operation?: string) => void;
  refreshAllQueries: (params?: ListQueriesParams) => Promise<void>; // GW-1459: Force refresh
}

const initialQueriesState = {
  datasetQueries: [],
  allQueries: [],
  currentQuery: null,
  queriesTotal: 0,
  allQueriesTotal: 0,
  queriesIsLoading: {},
  queriesHasLoaded: {},
  queriesLastLoaded: {},
  queriesErrors: {},
  queriesErrorStatuses: {},
};

export const createDatasetQueriesSlice: StateCreator<
  DatasetQueriesSlice,
  [],
  [],
  DatasetQueriesSlice
> = (set, get) => ({
  ...initialQueriesState,

  fetchQueries: async (datasetId: string, params?: ListQueriesParams) => {
    const operation = 'fetchQueries';
    const currentState = get();
    if (isOperationLoading(currentState.queriesIsLoading, operation)) return;
    if (hasOperationLoaded(currentState.queriesHasLoaded, operation)) return;

    await createAsyncSliceAction<DatasetQueriesSlice, ListQueriesResponse>(
      operation,
      () => datasetsApi.getDatasetQueries(datasetId, params),
      (response) => ({
        datasetQueries: response.queries,
        queriesTotal: response.total,
      }),
      set,
      get,
      { prefix: 'queries' }
    )();
  },

  fetchQueryById: async (queryId: string) => {
    const operation = 'fetchQueryById';
    const currentState = get();
    if (isOperationLoading(currentState.queriesIsLoading, operation)) return;

    await createAsyncSliceAction<DatasetQueriesSlice, DatasetQuery>(
      operation,
      () => datasetsApi.getQueryById(queryId),
      (query) => ({
        currentQuery: query,
      }),
      set,
      get,
      { prefix: 'queries' }
    )();
  },

  submitQuery: async (datasetId: string, request: SubmitQueryRequest) => {
    const currentState = get();

    set({
      queriesIsLoading: setLoading(currentState.queriesIsLoading, 'submitQuery', true),
      queriesErrors: clearOpError(currentState.queriesErrors, 'submitQuery'),
    });

    try {
      const response = await datasetsApi.submitQuery(datasetId, request);
      const state = get();
      set({
        queriesIsLoading: setLoading(state.queriesIsLoading, 'submitQuery', false),
      });
      return response;
    } catch (error) {
      const state = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit query';

      set({
        queriesIsLoading: setLoading(state.queriesIsLoading, 'submitQuery', false),
        queriesErrors: setError(state.queriesErrors, 'submitQuery', errorMessage),
      });
      throw error;
    }
  },

  clearQueries: () => {
    set({ datasetQueries: [], queriesTotal: 0 });
  },

  // GW-1459: Clear all queries
  clearAllQueries: () => {
    const currentState = get();
    set({
      allQueries: [],
      allQueriesTotal: 0,
      queriesHasLoaded: { ...currentState.queriesHasLoaded, fetchAllQueries: false },
    });
  },

  clearCurrentQuery: () => {
    set({ currentQuery: null });
  },

  clearQueriesError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ queriesErrors: clearOpError(currentState.queriesErrors, operation) });
    } else {
      set({ queriesErrors: {} });
    }
  },

  // GW-1459: Fetch all queries across datasets
  fetchAllQueries: async (params?: ListQueriesParams) => {
    const operation = 'fetchAllQueries';
    const currentState = get();
    if (isOperationLoading(currentState.queriesIsLoading, operation)) return;
    if (hasOperationLoaded(currentState.queriesHasLoaded, operation)) return;

    await createAsyncSliceAction<DatasetQueriesSlice, ListQueriesResponse>(
      operation,
      () => datasetsApi.getAllQueries(params),
      (response) => ({
        allQueries: response.queries,
        allQueriesTotal: response.total,
      }),
      set,
      get,
      { prefix: 'queries' }
    )();
  },

  // GW-1459: Force refresh all queries (ignores cache)
  refreshAllQueries: async (params?: ListQueriesParams) => {
    const operation = 'fetchAllQueries';
    if (isOperationLoading(get().queriesIsLoading, operation)) return;

    await createAsyncSliceAction<DatasetQueriesSlice, ListQueriesResponse>(
      operation,
      () => datasetsApi.getAllQueries(params),
      (response) => ({
        allQueries: response.queries,
        allQueriesTotal: response.total,
      }),
      set,
      get,
      { prefix: 'queries' }
    )();
  },
});
